import { useCallback, useEffect, useReducer } from 'react';
import useHandleAppException from '../../../../../../hooks/useHandleAppException';
import LangEnum from '../../../../../../lang/LangEnum';
import { useGetAllChatsNotifications } from '../../../../../../services/modules/chats/chats.queries';
import {
  findChats,
  findGroupedChats,
  findMainChatBetweenStoreAndBrand,
  updateLastViewedMessageByChatId,
} from '../../../../../../services/modules/chats/chats.repository';
import snackbarStore from '../../../../../../stores/Snackbar.store';
import { useApplicationContext } from '../../../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { useAbility } from '../../../../../users/AbilityContext';
import { Chat, CreateChatDto, MessageType, VideoCall } from '../../../../types/chat.types';
import chatRepository from '../../../../utils/chat.repository';
import { isPrivateChat } from '../../../../utils/chat.utils';
import { useChatSocketContext } from '../../../chatSocket/hooks/useChatSocketContext';
import { LastNotification } from '../../../chatSocket/reducer/chatSocketReducer.types';
import useBroadcastLastViewedChat from './hooks/useBroadcastLastViewedChat';
import { chatListInitialState, chatListReducer } from './reducer/chatListReducer';
import {
  addChatsToGroup,
  addNewChat,
  addVideoCallInRoom,
  errorFetchingGroups,
  fetchMoreResult,
  fetchingMore,
  overwriteNotifications,
  resetState,
  updateChatInfo,
  updateChatToTop,
  updateChatWithNotification,
  updateLastViewedMessageInChat,
} from './reducer/chatListReducer.actions';
import { ChatState } from './reducer/chatListReducer.types';
import { findChatByCaseIdInGroup, findChatByIdInGroup } from './reducer/chatListReducer.utils';

const DEFAULT_ITEMS_PER_PAGE = 8;

export const isMainChat = (chat: Chat) => isPrivateChat(chat.type);

const useChats = () => {
  const { translate } = useTranslation();
  const ability = useAbility();
  const hasChatReadPermission = !ability.isLoadingAbilities && ability.can('read', 'chats');
  const chatSocket = useChatSocketContext();
  const { handleErrorResponse } = useHandleAppException();
  const {
    team,
    state: { user },
  } = useApplicationContext();

  const { data: allChatsNotificationsIncomingFromApi, refetch: refetchChatsNotifications } =
    useGetAllChatsNotifications({ enabled: false });

  const [state, dispatch] = useReducer(chatListReducer, chatListInitialState);

  const { broadcastLastViewedMessage } = useBroadcastLastViewedChat({
    onReceiveLastViewedMessage: ({ chatId, messageId }) => {
      dispatch(updateLastViewedMessageInChat({ chatId, lastViewedMessageId: messageId }));
    },
  });

  const fetchMore = useCallback(async () => {
    if (!hasChatReadPermission || !state.hasMore) {
      return;
    }

    dispatch(fetchingMore());
    const response = await findGroupedChats({
      skip: state.chatRooms.length,
      limit: DEFAULT_ITEMS_PER_PAGE,
    });

    if (!response.ok || !response.data) {
      dispatch(errorFetchingGroups());
      return;
    }

    const groupedChats = response.data.data;
    const groupCount = response.data.count;

    const chatIdsToFetch = groupedChats
      .filter((group) => group.chatIdsPresentInThisGrouping.length === 1)
      .map((group) => group.chatIdsPresentInThisGrouping[0]);

    if (!chatIdsToFetch.length) {
      dispatch(fetchMoreResult({ groupedChats, groupCount }));
      return;
    }

    const responseOfFindChatsInGroupWithOneChat = await findChats({
      skip: 0,
      chatIds: chatIdsToFetch,
      limit: chatIdsToFetch.length,
    });

    dispatch(
      fetchMoreResult({
        groupedChats,
        groupCount,
        chatsToGroups: responseOfFindChatsInGroupWithOneChat.data?.data,
      }),
    );
  }, [hasChatReadPermission, state.chatRooms.length, state.hasMore]);

  const handleFindMainChatBetweenStoreAndBrand = async (brandId: string, storeId: string) => {
    const response = await findMainChatBetweenStoreAndBrand({
      brandId,
      storeId,
    });

    if (!response.ok || !response.data) {
      return;
    }

    return response.data;
  };

  const fetchChatsFromGroup = useCallback(
    async (groupMetaId: string) => {
      const group = state.chatRooms.find((chatRoom) => chatRoom.metaId === groupMetaId);

      if (!group) {
        return;
      }

      const chatIdsToFetch = group.chatIdsPresentInThisGrouping;

      const canFetchMore = chatIdsToFetch.length > group.chats.length + (group.mainChat ? 1 : 0);
      if (!canFetchMore) {
        return;
      }

      const alreadyFetchedChatIds = group.chats.map((chat) => chat.id);
      const alreadyFetchedChatIdsWithMainChat = group.mainChat
        ? [group.mainChat.id, ...alreadyFetchedChatIds]
        : alreadyFetchedChatIds;

      const chatIdsToFetchWithoutAlreadyFetched = chatIdsToFetch.filter(
        (chatId) => !alreadyFetchedChatIdsWithMainChat.includes(chatId),
      );

      const response = await findChats({
        skip: 0,
        limit: 10,
        chatIds: chatIdsToFetchWithoutAlreadyFetched,
      });

      if (!response.ok || !response.data) {
        return;
      }

      const chats = response.data.data;

      const alreadyHasMainChat = chats.some(isMainChat) || !!group.mainChat;

      const needFetchMainChat = !alreadyHasMainChat && group.chats.length === 0;

      const mainChat = needFetchMainChat
        ? await handleFindMainChatBetweenStoreAndBrand(group.brandInfo.id, group.storeInfo!.id)
        : undefined;

      const chatsWithMainChat = mainChat ? [mainChat, ...chats] : chats;

      dispatch(addChatsToGroup({ chats: chatsWithMainChat, groupMetaId }));
    },
    [state.chatRooms],
  );

  const handleUpdateChatWithNewInfo = useCallback((chat: Chat) => {
    dispatch(updateChatInfo({ chat }));
  }, []);

  const resetAllStates = async () => {
    dispatch(resetState());
  };

  const findGroupOfChatThatAlreadyExistsInAnyLoadedGroup = (chatId: string) =>
    state.chatRooms.find((group) => group.chatIdsPresentInThisGrouping.includes(chatId));

  const handleToAddNewChatInState = async (chat: Chat) => {
    const groupOfChat = findGroupOfChatThatAlreadyExistsInAnyLoadedGroup(chat.id);
    if (groupOfChat) {
      dispatch(addChatsToGroup({ chats: [chat], groupMetaId: groupOfChat.metaId }));
    } else {
      const groupOfChatLoadedFromApiResponse = await findGroupedChats({
        limit: 1,
        skip: 0,
        chatId: chat.id,
      });
      if (!groupOfChatLoadedFromApiResponse.ok || !groupOfChatLoadedFromApiResponse.data?.data) {
        //TODO: MELHORAR HANDLE DE ERRO NESSE FLUXO
        return null;
      }

      const group = groupOfChatLoadedFromApiResponse.data.data[0];

      if (!group) {
        return;
      }

      dispatch(addNewChat({ chat, groupOfChat: group }));
    }
  };

  const fetchSpecificChatById = async (chatId: string) => {
    const response = await chatRepository.getById(chatId);
    if (!response.ok || !response.data) {
      return;
    }

    const chat = response.data;

    await handleToAddNewChatInState(chat);

    return response.data;
  };

  const fetchSpecificChatByCaseId = async (caseId: string) => {
    const response = await findChats({ limit: 1, skip: 0, caseIds: [caseId] });
    if (!response.ok || !response.data || !response.data.data.length) {
      return;
    }

    const chat = response.data.data[0];

    await handleToAddNewChatInState(chat);

    return chat;
  };

  const handleReactivateChatOrAddNewChatById = async (chatId: string) => {
    const roomWithLoadedChat = state.chatRooms.find((chatRoom) =>
      findChatByIdInGroup(chatRoom, chatId),
    );
    const alreadyFetchedChat =
      roomWithLoadedChat && findChatByIdInGroup(roomWithLoadedChat, chatId);

    if (!roomWithLoadedChat || !alreadyFetchedChat) {
      return fetchSpecificChatById(chatId);
    }

    dispatch(
      updateChatToTop({
        chat: { ...alreadyFetchedChat, wasRemoved: false },
        websocketMessages: chatSocket.websocketMessages,
      }),
    );
  };

  const handleMoveChatToTop = async (chatId: string, wasRemoved?: boolean) => {
    const roomWithLoadedChat = state.chatRooms.find((chatRoom) =>
      findChatByIdInGroup(chatRoom, chatId),
    );
    const alreadyFetchedChat =
      roomWithLoadedChat && findChatByIdInGroup(roomWithLoadedChat, chatId);

    if (!roomWithLoadedChat || !alreadyFetchedChat) {
      if (!wasRemoved) {
        await fetchSpecificChatById(chatId);
      }
      return;
    }

    dispatch(
      updateChatToTop({
        chat: { ...alreadyFetchedChat, wasRemoved: !!wasRemoved },
        websocketMessages: chatSocket.websocketMessages,
      }),
    );
  };

  const handleUpdateChatsWithNotifications = async (notifications: LastNotification[]) => {
    const promisesToSolve = notifications.map(async (notification) => {
      const groupOfChat = state.chatRooms.find((group) =>
        findChatByIdInGroup(group, notification.chatId),
      );
      const alreadyFetchedChat =
        groupOfChat && findChatByIdInGroup(groupOfChat, notification.chatId);

      if (!alreadyFetchedChat) {
        await fetchSpecificChatById(notification.chatId);
      }

      const videoCall =
        notification.type === MessageType.VIDEO_CALL
          ? await chatRepository.getVideoCallByChatId(notification.chatId)
          : undefined;

      dispatch(
        updateChatWithNotification({
          newNotification: notification,
          videoCallStarted: videoCall?.ok ? videoCall.data : undefined,
        }),
      );
    });

    await Promise.allSettled(promisesToSolve);
  };

  const getChatById = async (chatId: string): Promise<ChatState | undefined> => {
    const groupOfChat = state.chatRooms.find((group) => {
      const chatsToFind = group.mainChat ? [group.mainChat, ...group.chats] : group.chats;
      return chatsToFind.some((groupChat) => groupChat.id === chatId);
    });

    if (!groupOfChat) {
      const room = await fetchSpecificChatById(chatId);
      if (room) {
        return { ...room, wasRemoved: false };
      }
      return;
    }

    const chatFound = findChatByIdInGroup(groupOfChat, chatId);

    return chatFound;
  };

  const getChatByCaseId = async (caseId: string): Promise<ChatState | undefined> => {
    const groupOfChat = state.chatRooms.find((group) => {
      const chatsToFind = group.mainChat ? [group.mainChat, ...group.chats] : group.chats;
      return chatsToFind.some((groupChat) => groupChat.caseId === caseId);
    });

    if (!groupOfChat) {
      const room = await fetchSpecificChatByCaseId(caseId);
      if (room) {
        return { ...room, wasRemoved: false };
      }
      return;
    }

    const chatFound = findChatByCaseIdInGroup(groupOfChat, caseId);

    return chatFound;
  };

  const getLoadedChatById = useCallback(
    (chatId: string) => {
      const groupOfChat = state.chatRooms.find((group) =>
        group.chatIdsPresentInThisGrouping.includes(chatId),
      );
      if (!groupOfChat) {
        return undefined;
      }
      return findChatByIdInGroup(groupOfChat, chatId);
    },
    [state.chatRooms],
  );

  const createChatIfNotExists = async (chat: CreateChatDto & { id?: string }) => {
    if (chat.id) {
      //TODO: NÃO ME PARECE FAZER SENTIDO CONTINUAR O FLUXO CASO UM CHAT COM ID NÃO SEJA ENCONTRADO NO getById
      const foundedChat = await getChatById(chat.id);
      if (foundedChat) {
        return foundedChat;
      }
    }

    const response = await chatRepository.createChat({
      type: chat.type,
      caseId: chat.caseId,
      participants: {
        brand: chat.participants?.brand,
        stores: chat.participants?.stores,
      },
    });

    if (!response.ok || !response.data) {
      const errorMessage = handleErrorResponse(response as any);
      snackbarStore.handleMessage({
        type: 'error',
        message: errorMessage || translate(LangEnum.CHAT_ERROR_ON_CREATE_CHAT),
      });
      return;
    }

    return {
      ...response.data,
      wasRemoved: false,
    };
  };

  const handleAddVideoCallInRoom = (chatId: string, videoCall: VideoCall) => {
    if (!user) {
      return;
    }
    dispatch(addVideoCallInRoom({ chatId, videoCall, user }));
  };

  const handleUpdateLastViewedMessageInChat = async (
    chatId: string,
    lastViewedMessageId: string,
  ) => {
    const isSameMessage =
      state.notifications.get(chatId)?.lastViewedChatMessageId === lastViewedMessageId;

    if (isSameMessage) {
      return;
    }

    broadcastLastViewedMessage({ chatId, messageId: lastViewedMessageId });
    dispatch(updateLastViewedMessageInChat({ chatId, lastViewedMessageId }));
    await updateLastViewedMessageByChatId(chatId, lastViewedMessageId);
  };

  const getCountOfChatsWithNotificationsByChatIds = useCallback(
    (chatIds: string[]) => {
      if (state.notifications.size === 0) {
        return 0;
      }

      const chatWithNotificationCount = chatIds.reduce((acc, chatId) => {
        const notification = state.notifications.get(chatId);
        return (
          acc + (notification?.unreadMessagesCount && notification.unreadMessagesCount > 0 ? 1 : 0)
        );
      }, 0);

      return chatWithNotificationCount;
    },
    [state.notifications],
  );

  const getTotalUnreadMessagesCount = useCallback(() => {
    if (state.notifications.size === 0) {
      return 0;
    }

    return Array.from(state.notifications.values()).reduce(
      (acc, notification) => acc + notification.unreadMessagesCount,
      0,
    );
  }, [state.notifications]);

  const getTotalUnreadMessageToChat = useCallback(
    (chatId: string) => {
      const notification = state.notifications.get(chatId);
      return notification?.unreadMessagesCount || 0;
    },
    [state.notifications],
  );

  useEffect(() => {
    if (!team) {
      return;
    }

    resetAllStates();
  }, [team?.id]);

  useEffect(() => {
    if (!hasChatReadPermission) {
      return;
    }

    if (team?.id && state.page === 0 && state.hasMore) {
      fetchMore();
      refetchChatsNotifications();
    }
  }, [
    hasChatReadPermission,
    state.hasMore,
    state.page,
    team?.id,
    fetchMore,
    refetchChatsNotifications,
  ]);

  useEffect(() => {
    dispatch(
      overwriteNotifications({
        notifications: allChatsNotificationsIncomingFromApi || [],
      }),
    );
  }, [allChatsNotificationsIncomingFromApi]);

  return {
    state,
    getChatById,
    resetAllStates,
    getChatByCaseId,
    getLoadedChatById,
    fetchChatsFromGroup,
    createChatIfNotExists,
    loadMoreRows: fetchMore,
    handleAddVideoCallInRoom,
    getTotalUnreadMessagesCount,
    getTotalUnreadMessageToChat,
    moveChatToTop: handleMoveChatToTop,
    handleUpdateChatsWithNotifications,
    handleUpdateLastViewedMessageInChat,
    handleReactivateChatOrAddNewChatById,
    getCountOfChatsWithNotificationsByChatIds,
    updateChatWithNewInfo: handleUpdateChatWithNewInfo,
  };
};

export default useChats;
